import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Brain,
  Gavel,
  Car,
  Users,
  Send,
  History as HistoryIcon,
  Play,
  StopCircle,
  Award,
  TrendingUp,
  MessageSquare,
  Loader2,
  ChevronRight,
  FileText,
  Lock,
  Crown,
  Shield,
  AlertTriangle
} from 'lucide-react';
import axios from 'axios';

const LegalSimulations = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Check if user has premium access
  const hasPremiumAccess = user?.subscription_plan === 'sovereign_shield' || 
                          user?.subscription_plan === 'defender_of_the_people' || 
                          user?.subscription_plan === 'justice_seeker' ||
                          user?.subscription_plan === 'lifetime' ||
                          user?.role === 'admin';

  // If no premium access, show upgrade message
  if (!hasPremiumAccess) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <Card className="border-2 border-purple-200">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-purple-100 rounded-full">
                <Lock className="w-12 h-12 text-purple-600" />
              </div>
            </div>
            <CardTitle className="text-3xl font-bold text-gray-900 mb-2">
              Legal Simulations - Premium Feature
            </CardTitle>
            <p className="text-lg text-gray-600">
              Upgrade your subscription to access AI-powered legal training simulations
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-purple-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Brain className="w-6 h-6 text-purple-600" />
                What You'll Get:
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <ChevronRight className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">
                    <strong>Interactive Training:</strong> Practice asserting your rights in realistic scenarios
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <ChevronRight className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">
                    <strong>Multiple Scenarios:</strong> Traffic stops, court hearings, police interactions, and more
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <ChevronRight className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">
                    <strong>AI Evaluation:</strong> Get detailed feedback on your constitutional and common law assertions
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <ChevronRight className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">
                    <strong>Performance Tracking:</strong> Review past simulations and track your progress
                  </span>
                </li>
              </ul>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white border border-purple-200 rounded-lg p-4 text-center">
                <Crown className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                <h4 className="font-semibold text-gray-900 mb-1">Justice Seeker</h4>
                <p className="text-2xl font-bold text-purple-600 mb-2">$9.99/mo</p>
                <Button 
                  onClick={() => navigate('/dashboard/account')}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  size="sm"
                >
                  Upgrade Now
                </Button>
              </div>

              <div className="bg-white border-2 border-purple-400 rounded-lg p-4 text-center relative">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-purple-600 text-white">Most Popular</Badge>
                </div>
                <Crown className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <h4 className="font-semibold text-gray-900 mb-1">Sovereign Shield</h4>
                <p className="text-2xl font-bold text-purple-600 mb-2">$99/mo</p>
                <Button 
                  onClick={() => navigate('/dashboard/account')}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  size="sm"
                >
                  Upgrade Now
                </Button>
              </div>

              <div className="bg-white border border-purple-200 rounded-lg p-4 text-center">
                <Crown className="w-8 h-8 text-gold-600 mx-auto mb-2" />
                <h4 className="font-semibold text-gray-900 mb-1">Defender of the People</h4>
                <p className="text-2xl font-bold text-purple-600 mb-2">$1497</p>
                <p className="text-xs text-gray-500 mb-2">One-time payment</p>
                <Button 
                  onClick={() => navigate('/dashboard/account')}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  size="sm"
                >
                  Upgrade Now
                </Button>
              </div>
            </div>

            <div className="text-center pt-4">
              <p className="text-sm text-gray-600 mb-4">
                All premium plans include access to Legal Simulations and other premium features
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard/account')}
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
              >
                View All Plans & Features
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  const [activeTab, setActiveTab] = useState('select'); // 'select', 'active', 'history', 'rankings'
  const [currentSimulation, setCurrentSimulation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [viewingSimulation, setViewingSimulation] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [rankingTiers, setRankingTiers] = useState([]);
  const messagesEndRef = useRef(null);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    } else if (activeTab === 'rankings') {
      fetchRankingData();
    }
  }, [activeTab]);

  const scenarios = [
    {
      type: 'traffic_stop',
      title: 'Traffic Stop',
      icon: Car,
      description: 'Practice asserting your rights during a traffic stop. Learn to distinguish traveling vs driving, handle consent searches, and stay composed.',
      color: 'blue'
    },
    {
      type: 'court_hearing',
      title: 'Court Hearing',
      icon: Gavel,
      description: 'Navigate courtroom procedures, challenge jurisdiction, and present yourself as a sovereign individual with honor.',
      color: 'purple'
    },
    {
      type: 'cps_encounter',
      title: 'CPS Encounter',
      icon: Users,
      description: 'Handle Child Protective Services investigations while protecting your family\'s rights and understanding the limits of their authority.',
      color: 'orange'
    },
    {
      type: 'admin_agency',
      title: '🧾 Admin & Agency',
      icon: FileText,
      description: 'Deal with administrative agencies and their notices. Handle IRS Letters, DMV Refusals, Utility Shut-Offs, and Land Notices while asserting your natural rights.',
      color: 'green'
    },
    {
      type: 'right_to_travel',
      title: '🛡️ Right to Travel',
      icon: Shield,
      description: 'Assert your fundamental right to travel freely. Practice encounters involving No Registration challenges and Private Plate encounters.',
      color: 'indigo'
    },
    {
      type: 'emergency',
      title: '⚠️ Emergency',
      icon: AlertTriangle,
      description: 'Handle high-stakes emergency situations. Practice Being Trespassed, resisting Forced Medical Treatment, and navigating Airport Encounters.',
      color: 'red'
    }
  ];

  const startSimulation = async (scenarioType) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE}/api/simulations/start`,
        { scenario_type: scenarioType, user_name: 'User' },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      setCurrentSimulation(response.data);
      setMessages([
        {
          role: 'ai',
          content: response.data.opening_message,
          timestamp: response.data.created_at
        }
      ]);
      setActiveTab('active');

      toast({
        title: 'Simulation Started',
        description: `You are now interacting with ${response.data.ai_role}`,
        variant: 'success'
      });
    } catch (error) {
      console.error('Error starting simulation:', error);
      toast({
        title: 'Failed to Start',
        description: error.response?.data?.detail || 'Could not start simulation',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!userInput.trim() || !currentSimulation) return;

    const userMessage = userInput.trim();
    setUserInput('');
    setSending(true);

    // Add user message to UI immediately
    setMessages(prev => [
      ...prev,
      {
        role: 'user',
        content: userMessage,
        timestamp: new Date().toISOString()
      }
    ]);

    try {
      const response = await axios.post(
        `${API_BASE}/api/simulations/message/${currentSimulation.simulation_id}`,
        { user_message: userMessage },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      // Add AI response
      setMessages(prev => [
        ...prev,
        {
          role: 'ai',
          content: response.data.ai_response,
          timestamp: new Date().toISOString()
        }
      ]);
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Message Failed',
        description: error.response?.data?.detail || 'Failed to send message',
        variant: 'destructive'
      });
    } finally {
      setSending(false);
    }
  };

  const endSimulation = async () => {
    if (!currentSimulation) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE}/api/simulations/end/${currentSimulation.simulation_id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      toast({
        title: 'Simulation Complete!',
        description: `Your score: ${response.data.score}/100`,
        variant: 'success'
      });

      // Show feedback modal or navigate to feedback view
      setCurrentSimulation({
        ...currentSimulation,
        score: response.data.score,
        feedback: response.data.feedback,
        completed: true
      });
    } catch (error) {
      console.error('Error ending simulation:', error);
      toast({
        title: 'Error',
        description: 'Failed to end simulation',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/simulations/history`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setHistory(response.data.simulations || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const viewSimulation = async (simulationId) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE}/api/simulations/${simulationId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setViewingSimulation(response.data.simulation);
    } catch (error) {
      console.error('Error viewing simulation:', error);
      toast({
        title: 'Error',
        description: 'Failed to load simulation',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRankingData = async () => {
    setLoading(true);
    try {
      // Fetch user stats
      const statsResponse = await axios.get(`${API_BASE}/api/simulations/rankings/user-stats`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setUserStats(statsResponse.data);

      // Fetch leaderboard
      const leaderboardResponse = await axios.get(`${API_BASE}/api/simulations/rankings/leaderboard`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setLeaderboard(leaderboardResponse.data);

      // Fetch tier information
      const tiersResponse = await axios.get(`${API_BASE}/api/simulations/rankings/tiers`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setRankingTiers(tiersResponse.data.tiers || []);
    } catch (error) {
      console.error('Error fetching ranking data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load ranking data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 75) return 'text-blue-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getRankBadgeColor = (tier) => {
    switch(tier) {
      case 5: return 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white';
      case 4: return 'bg-gradient-to-r from-purple-500 to-purple-700 text-white';
      case 3: return 'bg-gradient-to-r from-blue-500 to-blue-700 text-white';
      case 2: return 'bg-gradient-to-r from-green-500 to-green-700 text-white';
      case 1: return 'bg-gradient-to-r from-gray-400 to-gray-600 text-white';
      default: return 'bg-gray-300 text-gray-800';
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Brain className="w-8 h-8 text-purple-600" />
          <h1 className="text-3xl font-bold text-gray-900">Legal Simulation Engine</h1>
        </div>
        <p className="text-gray-600">
          Practice real legal scenarios and master your Constitutional rights through interactive roleplay
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        <button
          onClick={() => {
            setActiveTab('select');
            setCurrentSimulation(null);
            setViewingSimulation(null);
          }}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'select'
              ? 'text-purple-600 border-b-2 border-purple-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Play className="w-4 h-4 inline mr-2" />
          Scenarios
        </button>
        <button
          onClick={() => setActiveTab('rankings')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'rankings'
              ? 'text-purple-600 border-b-2 border-purple-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Award className="w-4 h-4 inline mr-2" />
          Rankings
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'history'
              ? 'text-purple-600 border-b-2 border-purple-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <HistoryIcon className="w-4 h-4 inline mr-2" />
          History
        </button>
      </div>

      {/* Select Scenario Tab */}
      {activeTab === 'select' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {scenarios.map((scenario) => {
            const Icon = scenario.icon;
            return (
              <Card
                key={scenario.type}
                className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-purple-500"
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Icon className={`w-6 h-6 text-${scenario.color}-600`} />
                    {scenario.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">{scenario.description}</p>
                  <Button
                    onClick={() => startSimulation(scenario.type)}
                    disabled={loading}
                    className={`w-full bg-${scenario.color}-600 hover:bg-${scenario.color}-700`}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Starting...
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Start Simulation
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Active Simulation Tab */}
      {activeTab === 'active' && currentSimulation && !currentSimulation.completed && (
        <div className="space-y-4">
          {/* Simulation Header */}
          <Card className="border-2 border-purple-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg">
                    Active Simulation: {currentSimulation.scenario_type.replace('_', ' ').toUpperCase()}
                  </h3>
                  <p className="text-sm text-gray-600">
                    You are interacting with: <span className="font-medium">{currentSimulation.ai_role}</span>
                  </p>
                </div>
                <Button
                  onClick={endSimulation}
                  disabled={loading}
                  variant="destructive"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <StopCircle className="w-4 h-4 mr-2" />
                  )}
                  End & Get Feedback
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Messages */}
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4 mb-4 max-h-[500px] overflow-y-auto">
                {messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-lg p-4 ${
                        msg.role === 'user'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      <p className="text-sm font-medium mb-1">
                        {msg.role === 'user' ? 'You' : currentSimulation.ai_role}
                      </p>
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 rounded-lg p-4">
                      <Loader2 className="w-5 h-5 animate-spin text-gray-600" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="flex gap-2">
                <Input
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder="Type your response..."
                  disabled={sending}
                />
                <Button onClick={sendMessage} disabled={sending || !userInput.trim()}>
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Feedback View */}
      {activeTab === 'active' && currentSimulation?.completed && (
        <Card className="border-2 border-green-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-6 h-6 text-yellow-600" />
              Simulation Complete - Your Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Overall Score */}
              <div className="text-center py-8 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg">
                <p className="text-gray-600 mb-2">Overall Score</p>
                <p className={`text-6xl font-bold ${getScoreColor(currentSimulation.score)}`}>
                  {currentSimulation.score}
                  <span className="text-3xl text-gray-400">/100</span>
                </p>
              </div>

              {/* Breakdown */}
              {currentSimulation.feedback?.breakdown && (
                <div>
                  <h3 className="font-semibold text-lg mb-4">Performance Breakdown</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(currentSimulation.feedback.breakdown).map(([key, value]) => (
                      <div key={key} className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600 capitalize">{key}</p>
                        <p className="text-2xl font-bold text-purple-600">{value}/25</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Strengths & Improvements */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {currentSimulation.feedback?.strengths && (
                  <div>
                    <h3 className="font-semibold text-green-700 mb-2 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      What You Did Well
                    </h3>
                    <ul className="space-y-2">
                      {currentSimulation.feedback.strengths.map((strength, i) => (
                        <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                          <ChevronRight className="w-4 h-4 text-green-600 mt-0.5" />
                          {strength}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {currentSimulation.feedback?.improvements && (
                  <div>
                    <h3 className="font-semibold text-orange-700 mb-2 flex items-center gap-2">
                      <Brain className="w-5 h-5" />
                      Areas for Improvement
                    </h3>
                    <ul className="space-y-2">
                      {currentSimulation.feedback.improvements.map((improvement, i) => (
                        <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                          <ChevronRight className="w-4 h-4 text-orange-600 mt-0.5" />
                          {improvement}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Missed Opportunities */}
              {currentSimulation.feedback?.missed_opportunities && currentSimulation.feedback.missed_opportunities.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h3 className="font-semibold text-yellow-800 mb-3 flex items-center gap-2">
                    <Award className="w-5 h-5" />
                    Missed Opportunities
                  </h3>
                  <ul className="space-y-2">
                    {currentSimulation.feedback.missed_opportunities.map((opportunity, i) => (
                      <li key={i} className="text-sm text-yellow-800 flex items-start gap-2">
                        <ChevronRight className="w-4 h-4 text-yellow-600 mt-0.5" />
                        {opportunity}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Equitable Rights Analysis */}
              {currentSimulation.feedback?.equitable_rights_analysis && (
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-400 rounded-lg p-6">
                  <h3 className="font-bold text-purple-900 text-xl mb-4 flex items-center gap-2">
                    <Gavel className="w-6 h-6" />
                    Constitutional, Common Law & Equity Analysis
                  </h3>
                  <p className="text-sm text-purple-800 mb-6 italic">
                    This analysis focuses on LAWFUL rights (Constitutional, Common Law, Equity) - NOT statutory "legal" rights
                  </p>
                  
                  {/* Constitutional Rights */}
                  {currentSimulation.feedback.equitable_rights_analysis.constitutional_rights_asserted && currentSimulation.feedback.equitable_rights_analysis.constitutional_rights_asserted.length > 0 && (
                    <div className="mb-4 bg-white rounded-lg p-4">
                      <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                        📜 Constitutional Rights You Asserted:
                      </h4>
                      <ul className="space-y-1 ml-4">
                        {currentSimulation.feedback.equitable_rights_analysis.constitutional_rights_asserted.map((item, i) => (
                          <li key={i} className="text-sm text-gray-800">✓ {item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Common Law Principles */}
                  {currentSimulation.feedback.equitable_rights_analysis.common_law_principles_used && currentSimulation.feedback.equitable_rights_analysis.common_law_principles_used.length > 0 && (
                    <div className="mb-4 bg-white rounded-lg p-4">
                      <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                        ⚖️ Common Law Principles You Used:
                      </h4>
                      <ul className="space-y-1 ml-4">
                        {currentSimulation.feedback.equitable_rights_analysis.common_law_principles_used.map((item, i) => (
                          <li key={i} className="text-sm text-gray-800">✓ {item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Equity Remedies */}
                  {currentSimulation.feedback.equitable_rights_analysis.equity_remedies_applied && currentSimulation.feedback.equitable_rights_analysis.equity_remedies_applied.length > 0 && (
                    <div className="mb-4 bg-white rounded-lg p-4">
                      <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                        🎯 Equity Remedies You Applied:
                      </h4>
                      <ul className="space-y-1 ml-4">
                        {currentSimulation.feedback.equitable_rights_analysis.equity_remedies_applied.map((item, i) => (
                          <li key={i} className="text-sm text-gray-800">✓ {item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Statutory Traps Avoided */}
                  {currentSimulation.feedback.equitable_rights_analysis.statutory_traps_avoided && currentSimulation.feedback.equitable_rights_analysis.statutory_traps_avoided.length > 0 && (
                    <div className="mb-4 bg-white rounded-lg p-4">
                      <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                        🛡️ Statutory Traps You Avoided:
                      </h4>
                      <ul className="space-y-1 ml-4">
                        {currentSimulation.feedback.equitable_rights_analysis.statutory_traps_avoided.map((item, i) => (
                          <li key={i} className="text-sm text-gray-800">✓ {item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Missed Opportunities */}
                  {currentSimulation.feedback.equitable_rights_analysis.missed_opportunities && currentSimulation.feedback.equitable_rights_analysis.missed_opportunities.length > 0 && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                      <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                        ⚠️ Missed Opportunities:
                      </h4>
                      <ul className="space-y-1 ml-4">
                        {currentSimulation.feedback.equitable_rights_analysis.missed_opportunities.map((item, i) => (
                          <li key={i} className="text-sm text-red-800">✗ {item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Concepts to Study */}
                  {currentSimulation.feedback.equitable_rights_analysis.concepts_to_study && currentSimulation.feedback.equitable_rights_analysis.concepts_to_study.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                        📚 Key Concepts to Study:
                      </h4>
                      <ul className="space-y-1 ml-4">
                        {currentSimulation.feedback.equitable_rights_analysis.concepts_to_study.map((concept, i) => (
                          <li key={i} className="text-sm text-blue-800">→ {concept}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <Button
                onClick={() => {
                  setCurrentSimulation(null);
                  setMessages([]);
                  setActiveTab('select');
                }}
                className="w-full"
              >
                Start New Simulation
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* History Tab */}
      {activeTab === 'history' && !viewingSimulation && (
        <div>
          {history.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <HistoryIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 font-medium">No simulations yet</p>
                <p className="text-sm text-gray-500 mt-1">
                  Start a simulation to practice your legal skills
                </p>
                <Button onClick={() => setActiveTab('select')} className="mt-4">
                  Start Your First Simulation
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {history.map((sim) => (
                <Card key={sim.simulation_id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{sim.scenario_title}</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {new Date(sim.created_at).toLocaleDateString()} •{' '}
                          {sim.message_count} messages
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge
                            className={
                              sim.status === 'completed'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }
                          >
                            {sim.status}
                          </Badge>
                          {sim.score && (
                            <span className={`font-bold ${getScoreColor(sim.score)}`}>
                              Score: {sim.score}/100
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        onClick={() => viewSimulation(sim.simulation_id)}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        View Transcript
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}


      {/* Rankings Tab */}
      {activeTab === 'rankings' && (
        <div className="space-y-6">
          {loading ? (
            <Card>
              <CardContent className="py-12 flex justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
              </CardContent>
            </Card>
          ) : (
            <>
              {/* User Stats Card */}
              {userStats && (
                <Card className="border-2 border-purple-200">
                  <CardHeader className="bg-gradient-to-r from-purple-50 to-blue-50">
                    <CardTitle className="flex items-center gap-3">
                      <Award className="w-6 h-6 text-purple-600" />
                      Your Ranking
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    {!userStats.has_data ? (
                      <div className="text-center py-8">
                        <Badge className="mb-4 px-4 py-2 text-lg bg-gray-200 text-gray-700">
                          Novice Naturalist
                        </Badge>
                        <p className="text-gray-600 mb-6">
                          Complete your first simulation to earn your first rank!
                        </p>
                        <Button onClick={() => setActiveTab('select')} className="bg-purple-600 hover:bg-purple-700">
                          <Play className="w-4 h-4 mr-2" />
                          Start Your First Simulation
                        </Button>
                      </div>
                    ) : (
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Left: Rank Badge */}
                        <div className="flex flex-col items-center justify-center text-center p-6 bg-gradient-to-br from-purple-100 to-blue-100 rounded-lg">
                          <Badge className={`px-6 py-3 text-xl font-bold mb-4 ${getRankBadgeColor(userStats.rank_tier)}`}>
                            {userStats.rank_name}
                          </Badge>
                          <p className="text-sm text-gray-600">
                            Tier {userStats.rank_tier} of 5
                          </p>
                        </div>

                        {/* Right: Stats */}
                        <div className="space-y-4">
                          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <span className="text-gray-700 font-medium">Total Simulations</span>
                            <span className="text-2xl font-bold text-purple-600">
                              {userStats.total_simulations}
                            </span>
                          </div>
                          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <span className="text-gray-700 font-medium">Average Score</span>
                            <span className={`text-2xl font-bold ${getScoreColor(userStats.average_score)}`}>
                              {userStats.average_score.toFixed(1)}
                            </span>
                          </div>

                          {/* Progress to Next Rank */}
                          {userStats.next_rank && userStats.rank_tier < 5 && (
                            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-700">
                                  Progress to {userStats.next_rank.name}
                                </span>
                                <span className="text-sm font-bold text-blue-600">
                                  {userStats.progress_to_next_rank}%
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-3">
                                <div
                                  className="bg-gradient-to-r from-purple-500 to-blue-500 h-3 rounded-full transition-all"
                                  style={{ width: `${userStats.progress_to_next_rank}%` }}
                                ></div>
                              </div>
                              <p className="text-xs text-gray-600 mt-2">
                                Requirements: {userStats.next_rank.simulations_range} simulations, 
                                Avg Score {userStats.next_rank.score_range}
                              </p>
                            </div>
                          )}

                          {userStats.rank_tier === 5 && (
                            <div className="mt-4 p-4 bg-gradient-to-r from-yellow-100 to-yellow-200 rounded-lg text-center">
                              <Crown className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                              <p className="font-bold text-yellow-800">
                                You've reached the highest rank!
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Leaderboard */}
              {leaderboard && leaderboard.top_users.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <TrendingUp className="w-6 h-6 text-purple-600" />
                      Global Leaderboard
                    </CardTitle>
                    <p className="text-sm text-gray-600">
                      Top {leaderboard.top_users.length} users ranked by tier and performance
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {leaderboard.top_users.map((user, index) => (
                        <div
                          key={user.user_id}
                          className={`flex items-center gap-4 p-4 rounded-lg transition-colors ${
                            user.user_id === (userStats?.user_id || '')
                              ? 'bg-purple-50 border-2 border-purple-300'
                              : 'bg-gray-50 hover:bg-gray-100'
                          }`}
                        >
                          {/* Rank Position */}
                          <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center">
                            {index === 0 && (
                              <div className="text-3xl">🥇</div>
                            )}
                            {index === 1 && (
                              <div className="text-3xl">🥈</div>
                            )}
                            {index === 2 && (
                              <div className="text-3xl">🥉</div>
                            )}
                            {index > 2 && (
                              <span className="text-xl font-bold text-gray-500">#{index + 1}</span>
                            )}
                          </div>

                          {/* User Info */}
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">
                              {user.user_name}
                              {user.user_id === (userStats?.user_id || '') && (
                                <span className="ml-2 text-purple-600 text-sm">(You)</span>
                              )}
                            </p>
                            <Badge className={`mt-1 ${getRankBadgeColor(user.rank_tier)}`}>
                              {user.rank_name}
                            </Badge>
                          </div>

                          {/* Stats */}
                          <div className="text-right">
                            <p className="text-sm text-gray-600">
                              {user.total_simulations} simulations
                            </p>
                            <p className={`font-bold ${getScoreColor(user.average_score)}`}>
                              Avg: {user.average_score.toFixed(1)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Current User Position if not in top */}
                    {leaderboard.current_user_position && !leaderboard.current_user_position.is_in_top && (
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <p className="text-sm text-gray-600 mb-2">Your Position:</p>
                        <div className="flex items-center gap-4 p-4 bg-purple-50 border-2 border-purple-300 rounded-lg">
                          <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center">
                            <span className="text-xl font-bold text-gray-500">
                              #{leaderboard.current_user_position.position}
                            </span>
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">You</p>
                            <Badge className={`mt-1 ${getRankBadgeColor(leaderboard.current_user_position.rank_tier)}`}>
                              {leaderboard.current_user_position.rank_name}
                            </Badge>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600">
                              {leaderboard.current_user_position.total_simulations} simulations
                            </p>
                            <p className={`font-bold ${getScoreColor(leaderboard.current_user_position.average_score)}`}>
                              Avg: {leaderboard.current_user_position.average_score.toFixed(1)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <p className="text-center text-sm text-gray-500 mt-4">
                      Total ranked users: {leaderboard.total_ranked_users}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Ranking Tiers Explanation */}
              {rankingTiers.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <Shield className="w-6 h-6 text-purple-600" />
                      Ranking Tiers
                    </CardTitle>
                    <p className="text-sm text-gray-600">
                      Progress through 5 tiers based on simulation count and average score
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {rankingTiers.map((tier) => (
                        <div
                          key={tier.tier}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            userStats?.rank_tier === tier.tier
                              ? 'border-purple-400 bg-purple-50'
                              : 'border-gray-200 bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <Badge className={`mb-2 ${getRankBadgeColor(tier.tier)}`}>
                                Tier {tier.tier}: {tier.name}
                              </Badge>
                              <p className="text-sm text-gray-700 mb-1">{tier.description}</p>
                              <p className="text-xs text-gray-600">
                                <strong>Requirements:</strong> {tier.simulations_range} simulations, 
                                Average score {tier.score_range}
                              </p>
                            </div>
                            {userStats?.rank_tier === tier.tier && (
                              <Badge className="bg-purple-600 text-white">Current Rank</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      )}

      {/* Viewing Simulation */}
      {activeTab === 'history' && viewingSimulation && (
        <div>
          <Button
            variant="outline"
            onClick={() => setViewingSimulation(null)}
            className="mb-4"
          >
            ← Back to History
          </Button>
          
          <Card>
            <CardHeader>
              <CardTitle>{viewingSimulation.scenario_title}</CardTitle>
              <p className="text-sm text-gray-600">
                {new Date(viewingSimulation.created_at).toLocaleString()}
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {viewingSimulation.messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-lg p-4 ${
                        msg.role === 'user'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      <p className="text-sm font-medium mb-1">
                        {msg.role === 'user' ? 'You' : viewingSimulation.ai_role}
                      </p>
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                      <p className="text-xs opacity-70 mt-2">
                        {new Date(msg.timestamp).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LegalSimulations;
